import { useMemo } from 'react'

import {
  matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CustomDeadlineModel, CustomDeadlineRequestOptions } from '@models/types'

import type { ModuleState } from '@redux/modules/types'

import { isCustomDeadlineToday } from '@functions/customDeadline'

import useCurrentUser from './useCurrentUser'

const watchEntityKeys = ['customDeadlines']

type CustomDeadlineFilters = {
  due_today?: boolean,
  owner_id?: number,
  owner_type?: string,
  string?: string,
}

type UseCustomDeadlinesOptions = {
  filters?: CustomDeadlineFilters,
  performHttpRequests?: boolean,
  requestOptions?: CustomDeadlineRequestOptions,
}

function useCustomDeadlines(options: UseCustomDeadlinesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    due_today: filterDueToday,
    string: filterString,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
  } = filters

  const {
    updatedEntities: { customDeadlines: customDeadlinesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { customDeadlines } = useSelector(reduxState => reduxState.entities)

  const { currentUser: { time_zone: userTimeZone } } = useCurrentUser()

  const filteredCustomDeadlines = useMemo(() => {
    const filtered = Object.values(customDeadlines).filter((customDeadline: CustomDeadlineModel) => {
      const {
        id, owner_id, owner_type, title,
      } = customDeadline

      const matchDueToday = filterDueToday ? isCustomDeadlineToday({
        customDeadline,
        // @ts-expect-error TS2322
        userTimeZone,
      }) : true

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? filterOwnerType === owner_type : true

      return matchDueToday && matchOwnerId && matchOwnerType && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [customDeadlinesUpdatedAt, JSON.stringify(filters)])

  const filteredCustomDeadlinesCount = filteredCustomDeadlines.length
  const hasFilteredCustomDeadlines = !!filteredCustomDeadlinesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCustomDeadlinesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCustomDeadlines } = useReduxAction(
    'customDeadlines',
    'loadCustomDeadlines',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCustomDeadlines,
    filteredCustomDeadlinesCount,
    hasFilteredCustomDeadlines,
    loading: loadingCustomDeadlines,
    userTimeZone,
  }
}

export type UseCustomDeadlinesPayload = ReturnType<typeof useCustomDeadlines>

export default useCustomDeadlines
